import { useState, type ReactNode } from 'react';
import './applications.scss';
import {
  sendMessage,
  errorMessage,
  type ApplicationCounts,
  type ApplicationGroup,
  type ApplicationStatusCode,
  type MessageItem,
  type TimelineItem,
  GROUP_TABS,
  STATUS_LABEL,
} from './applicationStatusApi';

const GROUP_OF: Record<ApplicationStatusCode, 'reviewing' | 'active' | 'closed'> = {
  SUBMITTED: 'reviewing',
  VIEWED: 'reviewing',
  ON_HOLD: 'reviewing',
  CONTACTED: 'active',
  INTERVIEW: 'active',
  OFFERED: 'active',
  ACCEPTED: 'closed',
  REJECTED: 'closed',
  WITHDRAWN: 'closed',
};

export function StatusBadge({ status }: { status: ApplicationStatusCode }) {
  const tone = status === 'OFFERED' || status === 'ACCEPTED' ? 'highlight' : status === 'REJECTED' || status === 'WITHDRAWN' ? 'muted' : GROUP_OF[status];
  return <span className={`app-status-badge app-status-badge--${tone}`}>{STATUS_LABEL[status] || status}</span>;
}

export function GroupTabs({ value, counts, onChange }: { value: ApplicationGroup; counts: ApplicationCounts | null; onChange: (g: ApplicationGroup) => void }) {
  return (
    <div className="app-group-tabs" role="tablist">
      {GROUP_TABS.map((tab) => (
        <button
          type="button"
          role="tab"
          key={tab.key}
          aria-selected={value === tab.key}
          className={`app-group-tabs__tab${value === tab.key ? ' is-active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label} <span>{counts ? counts[tab.key] : 0}</span>
        </button>
      ))}
    </div>
  );
}

/** 타임라인 — 노드 상태는 완료 · 현재 · 예정 3종만 (5장) */
export function Timeline({ items, showFuture }: { items: TimelineItem[]; showFuture?: string | null }) {
  return (
    <ol className="app-timeline">
      {items.map((item, idx) => {
        const current = idx === items.length - 1;
        return (
          <li key={`${item.status}-${idx}`} className={`app-timeline__node${current ? ' is-current' : ' is-done'}`}>
            <div className="app-timeline__head">
              <strong>{item.label}</strong>
              <time>{item.at}</time>
              {current && <em>현재</em>}
            </div>
            {item.interviewAt && <p className="app-timeline__memo">일시: {item.interviewAt}</p>}
            {item.memo && <p className="app-timeline__memo">{item.memo}</p>}
          </li>
        );
      })}
      {showFuture && (
        <li className="app-timeline__node is-future">
          <div className="app-timeline__head">
            <strong>{showFuture}</strong>
            <time>진행 시 이곳에 표시됩니다</time>
          </div>
        </li>
      )}
    </ol>
  );
}

export function MessageThread({
  applicationId,
  messages,
  canSend,
  disabledReason,
  onSent,
}: {
  applicationId: number;
  messages: MessageItem[];
  canSend: boolean;
  disabledReason?: string;
  onSent: (m: MessageItem) => void;
}) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const message = await sendMessage(applicationId, text);
      onSent(message);
      setBody('');
    } catch (error) {
      alert(errorMessage(error, '메시지를 보내지 못했습니다.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="app-messages">
      {messages.length === 0 ? (
        <p className="app-messages__empty">{canSend ? '아직 주고받은 메시지가 없습니다.' : disabledReason}</p>
      ) : (
        <ul className="app-messages__list">
          {messages.map((m) => (
            <li key={m.id} className={`app-messages__item${m.mine ? ' is-mine' : ''}`}>
              <p>{m.body}</p>
              <time>{m.from === 'CHURCH' ? '교회' : '지원자'} · {m.at}</time>
            </li>
          ))}
        </ul>
      )}
      {canSend && (
        <div className="app-messages__form">
          <textarea value={body} maxLength={2000} placeholder="메시지를 입력하세요" onChange={(e) => setBody(e.target.value)} />
          <button type="button" disabled={!body.trim() || sending} onClick={submit}>
            {sending ? '보내는 중...' : '보내기'}
          </button>
        </div>
      )}
    </div>
  );
}

/** 확인 대화상자 — 안전한 선택지를 왼쪽, 파괴적 액션은 붉은색 (5.2) */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = '취소',
  danger,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="app-dialog" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="app-dialog__box" onClick={(e) => e.stopPropagation()}>
        <h4>{title}</h4>
        <div className="app-dialog__body">{children}</div>
        <div className="app-dialog__actions">
          <button type="button" className="app-btn" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className={`app-btn ${danger ? 'app-btn--danger' : 'app-btn--primary'}`} disabled={busy} onClick={onConfirm}>
            {busy ? '처리 중...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
